import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { clsx } from "clsx";

import CloseIcon from "@src/stories/components/CloseIcon/CloseIcon";
import shopify from "frontend/js/shopify";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "@src/../entrypoints/theme";

export const ProductTileQuickAddModal = ({ product, onClose }) => {
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const [isVisible, setIsVisible] = useState(false);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [unavailableForShipping, setUnavailableForShipping] = useState<string[]>([]);

  // Trigger enter animation on next frame after mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const updateCurrentVariant = (variant) => {
    setCurrentVariant(variant);
  };
  
  useEffect(() => {
    async function fetchInvetoryDetails() {
      const variantIds = product?.variants?.map(variant => variant.id) || [];
      const response = await shopify.getInventoryDetails(variantIds);
      const nodes = response?.data?.nodes || []

      const _unavailableForShipping = nodes?.filter(node => {
        if (!node) return false;
        const inventoryLevels = node?.inventoryItem?.inventoryLevels?.nodes || [];
        const combinedStore = inventoryLevels.find(inventorylevel => inventorylevel.location?.name === "Combined Store");
        return !combinedStore || (combinedStore?.quantities?.[0]?.quantity ?? 0) <= 0;
      }).map(node => node?.id?.split("/").pop()) || [];

      setUnavailableForShipping(_unavailableForShipping);
    }

    fetchInvetoryDetails();
  }, [])

  if (product?.variants.length <= 1) return null;

  // ── Desktop: inline overlay on the tile (no portal) ──────────────────────
  if (!isMobile) {
    return (
      <div
        className="absolute bottom-0 left-0 w-full z-20 flex flex-col gap-4 p-4 backdrop-blur-[6px] cursor-default"
        style={{ background: "rgba(255, 255, 255, 0.80)" }}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
      >
        <CloseIcon
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
          className="absolute w-4 h-4 cursor-pointer top-4 right-4"
        />

        <p className="text-sm text-center">ADD SIZE (AU)</p>

        <div className="flex flex-wrap items-center justify-center gap-6">
          {product?.variants?.map((variant) => (
            <React.Fragment key={variant.id}>
              {variant.available && !unavailableForShipping?.includes(`${variant.id}`) ? (
                <button
                  className={clsx("flex cursor-pointer text-sm p-0 h-auto font-normal hover:underline", {
                    'underline': currentVariant?.id === variant.id
                  })}
                  style={{ minHeight: 0 }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateCurrentVariant(variant); }}
                >
                  {variant.options[1]}
                </button>
              ) : (
                <span className="text-sm font-normal text-black/40">{variant.options[1]}</span>
              )}
            </React.Fragment>
          ))}
        </div>

        <ajax-cart-product-form>
          <form
            action="/cart/add"
            id="product_form_id"
            className="w-full text-center"
            noValidate
            data-prodify-product-form
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          >
            <input type="hidden" name="id" value={`${currentVariant?.id}`} />
            <button
              type="submit"
              className="hover:underline w-auto cursor-pointer disabled:opacity-50 disabled:pointer-events-none text-sm font-normal p-0 h-auto"
              style={{ minHeight: 0 }}
              disabled={!currentVariant}
              onClick={(e) => e.stopPropagation()}
            >
              ADD TO BAG
            </button>
            <div data-ajax-cart-errors="form" className="text-xs text-error-1"></div>
          </form>
        </ajax-cart-product-form>
      </div>
    );
  }

  // ── Mobile: portal to <main> with backdrop + slide-up bottom sheet ────────
  const mainEl = document.querySelector('main') || document.body;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className={clsx(
          "fixed inset-0 bg-[#54565A88] z-40 transition-opacity duration-300",
          { "opacity-100": isVisible, "opacity-0 pointer-events-none": !isVisible }
        )}
        onClick={handleClose}
      />
      {/* Bottom sheet */}
      <div
        className={clsx(
          "fixed bottom-0 left-0 right-0 z-50 bg-white transition-transform duration-300 pt-10 pb-8 px-6",
          { "translate-y-0": isVisible, "translate-y-full": !isVisible }
        )}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
      >
        <CloseIcon
          onClick={handleClose}
          className="absolute w-4 h-4 cursor-pointer top-4 right-4"
        />

        <p className="text-xs text-center font-medium mb-2">{product.title}</p>
        <p className="text-xs text-center mb-6 text-[#54565A]">SELECT SIZE (AU)</p>

        <div className="flex flex-wrap items-center justify-center gap-6 mb-6">
              {product?.variants?.map((variant) => (
                <React.Fragment key={variant.id}>
                {variant.available && !unavailableForShipping?.includes(`${variant.id}`) ? (
                  <button
                    className={clsx("flex cursor-pointer text-sm p-0 h-auto min-h-full font-normal hover:underline", {
                      'underline': currentVariant?.id === variant.id
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      updateCurrentVariant(variant);
                    }}
                    style={{ minHeight: 0 }}
                  >
                    {variant.options[1]}
                  </button>
                ) : (
                  <span className="text-sm font-normal text-black/40">
                    {variant.options[1]}
                  </span>
                )}
              </React.Fragment>
              ))}
            </div>

        <ajax-cart-product-form>
          <form
            action="/cart/add"
            id="product_form_id"
            className="w-full"
            noValidate
            data-prodify-product-form
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          >
            <input type="hidden" name="id" value={`${currentVariant?.id}`} />

            <button
              type="submit"
              className="button-primary w-full [&:not(:disabled)]:bg-[#00263E] text-sm font-normal"
              disabled={!currentVariant}
              onClick={(e) => e.stopPropagation()}
            >
              <span>ADD TO BAG</span>
              <span className="icon-arrow-right"></span>
            </button>

            <div data-ajax-cart-errors="form" className="text-xs text-error-1"></div>
          </form>
        </ajax-cart-product-form>
      </div>
    </>,
    mainEl
  );
}

export default ProductTileQuickAddModal;